import React, { useState } from "react";
import FileUploader from "../Bulletin/CreatePost/FileUploader";
import { useCreateScrapbookPostMutation } from "../../../store/slices/api";
import { CATEGORIES, DEFAULT_CATEGORY } from "./categories";
import { tidy } from "./tidy";
import StylePicker from "./styles/StylePicker";
import StyleFields from "./styles/StyleFields";
import LivePreview from "./styles/LivePreview";
import { DEFAULT_STYLE, fitProblem } from "./styles/registry";

const MAX_CAPTION = 500;

/**
 * Posting to the wall.
 *
 * The uploader is the Bulletin's, unchanged — it already handles images, video
 * and PDF through Cloudinary (up to the plan's limits: 10 MB, 100 MB for video) and has been in front of the family for
 * months. Writing a second one would have meant two upload paths to keep in step
 * and two places for a file-type bug to hide.
 */
function CreatePost({ onPosted }) {
  // Closed until you mean it. A permanently open form with an upload widget and
  // an empty textarea is a demand; a single line is an invitation. This is the
  // whole difference between the Bulletin and this section.
  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [caption, setCaption] = useState("");
  const [place, setPlace] = useState("");
  const [whenText, setWhenText] = useState("");
  const [extraNote, setExtraNote] = useState("");
  const [category, setCategory] = useState(DEFAULT_CATEGORY);
  // The post's template, and each template's own boxes kept SEPARATELY, under
  // that template's key. Nothing leaks from one template into another, and
  // wandering off to look at a different one does not throw away what you have
  // already typed — the style menu promises "You can change it any time before
  // sharing", and it used to clear all five placard boxes without a word
  // (found by an audit of this PR, 2026-09-16).
  const [style, setStyle] = useState(DEFAULT_STYLE);
  const [detailsByStyle, setDetailsByStyle] = useState({});
  const styleDetails = detailsByStyle[style] || {};
  const setStyleDetails = (next) => setDetailsByStyle((all) => ({ ...all, [style]: next }));
  const [error, setError] = useState("");
  // True while any file is still on its way to Cloudinary. Share waits for it:
  // otherwise the post goes out with the first photo and silently without the
  // second.
  const [uploading, setUploading] = useState(false);
  // Bumped after a post goes out, which gives the uploader a fresh start: any
  // notice about a file that was refused belongs to the post just shared, not
  // the next one.
  const [uploaderKey, setUploaderKey] = useState(0);
  const [createPost, { isLoading }] = useCreateScrapbookPostMutation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (uploading) return;
    if (problem) return;
    if (attachments.length === 0) {
      setError("Add a photo, a clip or a file first — this is a picture wall.");
      return;
    }
    try {
      await createPost({
        caption: caption.trim(),
        place: place.trim(),
        when_text: whenText.trim(),
        extra_note: extraNote.trim(),
        category,
        attachments,
        style,
        style_details: styleDetails,
      }).unwrap();
      setAttachments([]);
      setCaption("");
      setPlace("");
      setWhenText("");
      setExtraNote("");
      setCategory(DEFAULT_CATEGORY);
      setStyle(DEFAULT_STYLE);
      setDetailsByStyle({});
      setUploaderKey((k) => k + 1);
      if (onPosted) onPosted();
    } catch (err) {
      setError(err?.data?.error || "That didn’t post. Try again in a moment.");
    }
  };

  const problem = attachments.length > 0 ? fitProblem(style, attachments, styleDetails) : null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-[11px] text-left mb-[26px]
                   bg-white dark:bg-neutral-900 border border-dupontGoldLt/30
                   rounded-[22px] px-[15px] py-[11px] shadow-sm
                   transition-[transform,box-shadow] duration-200 ease-out
                   hover:-translate-y-[2px] hover:shadow-md
                   motion-reduce:transition-none motion-reduce:hover:translate-y-0
                   [@media(hover:none)]:hover:translate-y-0"
      >
        <span
          aria-hidden="true"
          className="grid place-items-center w-[23px] h-[23px] rounded-full shrink-0
                     bg-dupontCream dark:bg-neutral-800 text-dupontBlue dark:text-dupontGoldLt
                     font-blairBody text-[15px] leading-none"
        >
          +
        </span>
        <span className="font-body italic text-[16px] text-gray-400">Add a photo or write a post…</span>
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white dark:bg-neutral-900 rounded-[14px] border border-dupontGoldLt/30
                 p-[16px] mb-[26px] shadow-md"
    >
      {/* A way out that you can see from the top of the form. "never mind" sat
          at the bottom, past everything, which is no way out at all (Sam,
          2026-09-16: "a simple x to go back"). */}
      <div className="flex items-baseline gap-[10px] mb-[11px]">
        <h3 className="m-0 font-blairBody text-[12px] font-semibold tracking-[0.14em] uppercase text-dupontGoldDk">
          Add to the scrapbook
        </h3>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close without posting"
          className="ml-auto -mt-[4px] -mr-[3px] px-[7px] text-[23px] leading-none
                     text-gray-400 hover:text-dupontBlue focus:outline-none
                     focus:ring-2 focus:ring-dupontBlue dark:focus:ring-dupontGoldLt rounded-[6px]"
        >
          ×
        </button>
      </div>

      <FileUploader
        key={uploaderKey}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
        onUploadingChange={setUploading}
      />

      <textarea
        value={caption}
        maxLength={MAX_CAPTION}
        onChange={(e) => setCaption(e.target.value)}
        rows={2}
        placeholder="Your thoughts about this (optional)…"
        aria-label="Caption"
        className="w-full mt-[12px] px-[11px] py-[9px] rounded-[8px] font-body text-[17px] resize-y
                   bg-white dark:bg-neutral-800 border border-dupontGoldLt/40
                   text-dupontBlue dark:text-darkText
                   focus:outline-none focus:ring-2 focus:ring-dupontBlue dark:focus:ring-dupontGoldLt"
      />

      <div className="mt-[11px] flex flex-wrap items-center gap-[10px]">
        <span className="text-[13px] text-gray-600 dark:text-gray-400">Style</span>
        <StylePicker value={style} onChange={setStyle} />
      </div>
      <StyleFields style={style} details={styleDetails} attachments={attachments} onChange={setStyleDetails} />
      {problem && !uploading && (
        <p className="mt-[8px] font-blairBody text-[13px] text-amber-700 dark:text-amber-400">{problem}</p>
      )}
      <LivePreview style={style} caption={caption} place={place} whenText={whenText}
                   extraNote={extraNote} attachments={attachments} details={styleDetails} />

      {/* Where / When / What else.
          Free text on purpose: a date picker would refuse "1987 (?)" and "the
          year we all wore white", which is most of what anyone actually knows
          about an old photograph.
          Tidied on BLUR, never while typing — correcting somebody mid-keystroke
          is the most watched-over-the-shoulder thing an input can do. */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.3fr] gap-[8px]
                      mt-[11px] pt-[11px] border-t border-dashed border-dupontGoldLt/40">
        {[
          ["Where?", place, setPlace],
          ["When?", whenText, setWhenText],
          ["What else?", extraNote, setExtraNote],
        ].map(([ph, val, set]) => (
          <input
            key={ph}
            type="text"
            value={val}
            maxLength={120}
            placeholder={ph}
            aria-label={ph.replace("?", "")}
            onChange={(e) => set(e.target.value)}
            onBlur={() => set((v) => tidy(v))}
            className="px-[11px] py-[8px] rounded-[8px] font-body text-[15px]
                       bg-white dark:bg-neutral-800 border border-dupontGoldLt/40
                       text-dupontBlue dark:text-darkText placeholder:italic placeholder:text-gray-400
                       focus:outline-none focus:ring-2 focus:ring-dupontBlue dark:focus:ring-dupontGoldLt"
          />
        ))}
        <p className="sm:col-span-3 font-blairBody text-[12px] text-gray-400 -mt-[2px]">
          All optional — whatever you know is enough.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-[10px] mt-[11px]">
        <label htmlFor="scrapbook-category" className="text-[13px] text-gray-600 dark:text-gray-400">
          Category
        </label>
        <select
          id="scrapbook-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-[10px] py-[6px] rounded-[6px] text-[13.5px]
                     bg-white dark:bg-neutral-800 border border-dupontGoldLt/40
                     text-dupontBlue dark:text-darkText"
        >
          {CATEGORIES.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>

        {/* The reason Share is greyed out, said NEXT TO Share. It is also shown
            up beside the boxes it is about, but that is four blocks up the form
            — from down here the button just looks broken. */}
        {problem && !uploading && (
          <span className="font-blairBody text-[13px] text-amber-700 dark:text-amber-400">{problem}</span>
        )}
        <button
          type="submit"
          disabled={isLoading || uploading || attachments.length === 0 || !!problem}
          className="ml-auto px-[18px] py-[8px] rounded-[8px] font-blairBody text-[13.5px] font-semibold tracking-[0.03em]
                     bg-dupontBlue text-white disabled:opacity-40
                     dark:bg-dupontGoldLt dark:text-dupontBlue"
        >
          {isLoading ? "Posting…" : "Share"}
        </button>
      </div>

      {error && <p className="mt-[9px] text-[13px] text-red-600">{error}</p>}
    </form>
  );
}

export default CreatePost;
